import { redirect } from '@tanstack/react-router';
import type { PermissionString } from '@vagusx/shared';
import { mePermissionsQueryOptions } from '@/lib/permissions-query';
import { queryClient } from '@/lib/query-client';

/**
 * Route-level permission guards, for any page (`/campaigns` included);
 * `setup-route-guard.ts` builds the Setup pages' guard on these.
 */

/**
 * Resolves the current user's permissions, or null if the fetch fails
 * (network error, expired session). Guards treat null as "no access" and
 * redirect to the dashboard — a degraded-but-navigable state beats an
 * uncaught error surfacing the router's error boundary. The backend still
 * enforces real permissions on every request.
 */
export async function fetchPermissions(): Promise<{
  permissions: string[];
  isSystemAdmin: boolean;
} | null> {
  try {
    return await queryClient.ensureQueryData(mePermissionsQueryOptions);
  } catch {
    return null;
  }
}

/**
 * Route-level guard for one permission (system admins always pass), for pages
 * outside Setup too — `/campaigns` (ADR-318, H). Redirects to the dashboard.
 */
export async function requireRoutePermission(permission: PermissionString): Promise<void> {
  const data = await fetchPermissions();

  if (data?.isSystemAdmin) return;
  if (data?.permissions.includes(permission)) return;

  throw redirect({ to: '/' });
}
